import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { inr } from "@/lib/format";
import { Trash2, Plus, Minus, ShoppingBag } from "lucide-react";

const FREE_SHIPPING = 2999;

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, removeItem, subtotal, lineKey } = useCart();
  const navigate = useNavigate();

  const remaining = Math.max(FREE_SHIPPING - subtotal, 0);
  const pct = Math.min((subtotal / FREE_SHIPPING) * 100, 100);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md bg-zinc-950 border-zinc-800 flex flex-col p-0" data-testid="cart-drawer">
        <SheetHeader className="px-5 py-4 border-b border-zinc-800">
          <SheetTitle className="font-display uppercase tracking-tight text-xl text-left">Your Bag ({items.length})</SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 text-zinc-500">
            <ShoppingBag className="h-10 w-10" />
            <p className="font-mono text-xs uppercase tracking-widest">Your bag is empty</p>
            <button
              onClick={() => { setOpen(false); navigate("/shop"); }}
              className="rounded-full bg-brand text-black px-5 py-2 text-xs font-semibold uppercase tracking-wide active:scale-95 transition"
              data-testid="cart-empty-shop-btn"
            >
              Start Shopping
            </button>
          </div>
        ) : (
          <>
            <div className="px-5 pt-4">
              <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                <div className="h-full bg-brand transition-all duration-500" style={{ width: `${pct}%` }} />
              </div>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-zinc-400">
                {remaining > 0 ? `Add ${inr(remaining)} for free shipping` : "You've unlocked free shipping"}
              </p>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {items.map((i) => {
                const k = lineKey(i);
                return (
                  <div key={k} className="flex gap-3" data-testid={`cart-item-${i.product_id}`}>
                    <img src={i.image} alt={i.name} className="h-24 w-20 object-cover bg-zinc-900" />
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between gap-2">
                        <h4 className="text-sm font-medium text-zinc-100 truncate">{i.name}</h4>
                        <button onClick={() => removeItem(k)} data-testid={`cart-remove-${i.product_id}`} className="text-zinc-500 hover:text-red-400">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                        {i.size}{i.color ? ` · ${i.color}` : ""}
                      </p>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center border border-zinc-800">
                          <button onClick={() => updateQty(k, Math.max(1, i.qty - 1))} className="p-1.5 hover:text-brand" data-testid={`cart-dec-${i.product_id}`}><Minus className="h-3 w-3" /></button>
                          <span className="px-3 font-mono text-xs">{i.qty}</span>
                          <button onClick={() => updateQty(k, Math.min(20, i.qty + 1))} className="p-1.5 hover:text-brand" data-testid={`cart-inc-${i.product_id}`}><Plus className="h-3 w-3" /></button>
                        </div>
                        <span className="font-mono text-sm">{inr(i.price * i.qty)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="border-t border-zinc-800 px-5 py-4 space-y-4">
              <div className="flex justify-between font-mono text-sm">
                <span className="text-zinc-400 uppercase tracking-widest text-xs">Subtotal</span>
                <span data-testid="cart-subtotal">{inr(subtotal)}</span>
              </div>
              <button
                onClick={() => { setOpen(false); navigate("/checkout"); }}
                data-testid="checkout-btn"
                className="w-full rounded-full bg-brand text-black py-3 text-sm font-bold uppercase tracking-wide hover:bg-brand-hover active:scale-95 transition"
              >
                Checkout · {inr(subtotal)}
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
